import React, { cloneElement, isValidElement } from "react";

import { cx, styled } from "simplydo/react/styled";

import { Dropdown } from "../dropdown";
import { UIProvider } from "../utils/UIContext";
import { SIZE_FONT } from "../utils/colors";
import { Button } from "./Button";
import type { ButtonGroupProps, ButtonProps } from "./Button.types";
import { ButtonLink } from "./ButtonLink";

const StyledButtonGroup = styled.div<{
  $size?: ButtonProps["size"];
  $fluid?: boolean;
}>`
  display: inline-flex;
  flex-direction: row;
  vertical-align: middle;
  font-size: ${({ $size }) => SIZE_FONT[$size || "medium"] || "1rem"};

  &.is-fluid-group {
    display: flex;
    width: 100%;
  }

  & > ${Button}:not(:only-child),
  & > ${ButtonLink}:not(:only-child),
  & > ${Dropdown}:not(:only-child) > ${Button},
  & > ${Dropdown}:not(:only-child) > ${ButtonLink} {
    margin: 0;
  }

  & > ${Button}:not(:first-child) {
    margin-left: -1px;
  }

  &.is-fluid-group > button,
  &.is-fluid-group > a {
    flex: 1 0 auto;
  }

  & > ${Button}:first-child:not(:only-child),
  & > ${ButtonLink}:first-child:not(:only-child),
  & > ${Dropdown}:first-child:not(:only-child) > ${Button} {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  & > ${Button}:last-child:not(:only-child),
  & > ${ButtonLink}:last-child:not(:only-child),
  & > ${Dropdown}:last-child:not(:only-child) > ${Button} {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  & > ${Button}:not(:first-child):not(:last-child),
  & > ${ButtonLink}:not(:first-child):not(:last-child),
  & > ${Dropdown}:not(:first-child):not(:last-child) > ${Button} {
    border-radius: 0;
  }

  &.is-outline-group > ${Button},
  &.is-outline-group > ${ButtonLink},
  &.is-outline-group > ${Dropdown} > ${Button} {
    box-shadow: 0 0 0 1px rgba(34, 36, 38, 0.15) inset;
  }
`;

const ButtonGroupComponent = (props: ButtonGroupProps) => {
  const {
    children,
    variant = "regular",
    color,
    size,
    fluid,
    className,
    style,
  } = props;

  // Pass variant to the buttons
  const buttonProps = { variant, color, size };
  const mappedChildren = React.Children.map(children, (child) => {
    if (isValidElement(child)) {
      return cloneElement(child, {
        ...buttonProps,
        ...(child.props as ButtonProps),
      });
    }
    return child;
  });

  return (
    <UIProvider value={{ size }}>
      <StyledButtonGroup
        $size={size}
        $fluid={fluid}
        className={cx(
          fluid && "is-fluid-group",
          variant === "outline" && "is-outline-group",
          className,
        )}
        style={style}
      >
        {mappedChildren}
      </StyledButtonGroup>
    </UIProvider>
  );
};

export const ButtonGroup = styled(ButtonGroupComponent)``;
